import { useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, RefreshCw, ClipboardList } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Badge } from '@/app/components/ui/badge';
import { Card, CardContent } from '@/app/components/ui/card';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/app/components/ui/pagination';
import { useSafeWorkPlans } from '@/hooks/use-safety';
import { formatDateOnly } from '@/lib/date-utils';
import type { SafeWorkPlanStatus } from '@/lib/types';
import { usePermissions } from '@/hooks/use-permissions';

const statusColors: Record<SafeWorkPlanStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  active: 'bg-blue-100 text-blue-800',
  closed: 'bg-gray-100 text-gray-600',
  archived: 'bg-gray-100 text-gray-500',
};

const statusLabels: Record<SafeWorkPlanStatus, string> = {
  draft: 'Draft',
  pending_review: 'Awaiting approval',
  approved: 'Approved',
  active: 'Active',
  closed: 'Closed',
  archived: 'Archived',
};

const PAGE_SIZE = 20;

export function WorkPlansPage() {
  const { hasMinModuleRole } = usePermissions();
  const canManageSafety = hasMinModuleRole('safety', 'manager');
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  const page = parseInt(searchParams.get('page') || '1', 10);
  const status = (searchParams.get('status') || '') as SafeWorkPlanStatus | '';
  const q = searchParams.get('q') || '';

  const { data, isLoading, isError, refetch } = useSafeWorkPlans({
    page,
    page_size: PAGE_SIZE,
    q,
    status,
  });

  const plans = data?.results || [];
  const totalCount = data?.count || 0;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const updateSearchParams = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!updates.page) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearch = useCallback(() => {
    updateSearchParams({ q: searchInput, page: '1' });
  }, [searchInput, updateSearchParams]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSearch();
      }
    },
    [handleSearch]
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Safe Work Plans</h1>
          <p className="text-gray-600 mt-1">
            Method statements and rigging plans
            {totalCount > 0 && ` (${totalCount} total)`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canManageSafety && (
            <Button asChild>
              <Link to="/safety/work-plans/new">
                <Plus className="h-4 w-4 mr-2" />
                New Work Plan
              </Link>
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search work plans..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="pl-10"
              />
            </div>
            <Select
              value={status}
              onValueChange={(value) =>
                updateSearchParams({ status: value === 'all' ? '' : value })
              }
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="pending_review">Awaiting approval</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch}>Search</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-red-600">
              Failed to load work plans. Please try again.
            </div>
          ) : plans.length === 0 ? (
            <div className="p-12 text-center">
              <ClipboardList className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600">No safe work plans found.</p>
              {canManageSafety && (
                <Button asChild className="mt-4">
                  <Link to="/safety/work-plans/new">Create your first work plan</Link>
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Access Method</TableHead>
                  <TableHead>Created By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <Link
                        to={`/safety/work-plans/${plan.id}`}
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {plan.title}
                      </Link>
                      {plan.project_name && (
                        <p className="text-sm text-gray-500">{plan.project_name}</p>
                      )}
                    </TableCell>
                    <TableCell>v{plan.version ?? 1}</TableCell>
                    <TableCell className="text-gray-600">{plan.location}</TableCell>
                    <TableCell className="text-gray-600">
                      {formatDateOnly(plan.date)}
                    </TableCell>
                    <TableCell>
                      <Badge className={plan.is_superseded ? 'bg-gray-100 text-gray-600' : statusColors[plan.status]}>
                        {plan.is_superseded ? 'Superseded' : statusLabels[plan.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-gray-600 capitalize">
                      {plan.access_method.replace('_', ' ')}
                    </TableCell>
                    <TableCell className="text-gray-600">{plan.created_by_username}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => updateSearchParams({ page: String(Math.max(1, page - 1)) })}
                className={page <= 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
              />
            </PaginationItem>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => updateSearchParams({ page: String(pageNum) })}
                    isActive={page === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              );
            })}
            <PaginationItem>
              <PaginationNext
                onClick={() =>
                  updateSearchParams({ page: String(Math.min(totalPages, page + 1)) })
                }
                className={
                  page >= totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </div>
  );
}
